import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { parseFraction, cmToInches } from "./lib/fractions";
import { createShopifyCheckout } from "./lib/shopify";

// Components
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { ProcessingOverlay } from "./components/ProcessingOverlay";
import { ChatWindow } from "./components/ChatWindow";
import { OrderBuilder } from "./components/OrderBuilder";
import { QuoteSummary } from "./components/QuoteSummary";

// Modals
import { 
  ContactModal, 
  CallbackModal, 
  EmailModal, 
  SaveQuoteModal, 
  LoadQuoteModal, 
  ImageViewerModal,
  UnitSelectModal
} from "./components/modals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  // Door styles data
  const [doorStyles, setDoorStyles] = useState({});
  
  // Parts/doors state
  const [doors, setDoors] = useState([]);
  const [units, setUnits] = useState("imperial");
  const prevUnitsRef = useRef(units);
  
  // Quote state
  const [region, setRegion] = useState("central_canada");
  const [quote, setQuote] = useState(null);
  const [isCalc, setIsCalc] = useState(false);
  const [includePackaging, setIncludePackaging] = useState(false);
  const [savedQuoteId, setSavedQuoteId] = useState(null);
  
  // Chat state
  const [msgs, setMsgs] = useState([]);
  const [chatIn, setChatIn] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessId, setSessId] = useState(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const chatRef = useRef(null);
  const isInitialLoad = useRef(true);
  
  // Modal states
  const [showContactModal, setShowContactModal] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  
  // Form states
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [callbackForm, setCallbackForm] = useState({ name: "", phone: "", time: "", notes: "" });
  const [emailForm, setEmailForm] = useState({ name: "", email: "" });
  const [saveForm, setSaveForm] = useState({ name: "", email: "" });
  const [loadId, setLoadId] = useState("");
  
  // Image viewer state
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedStyleName, setSelectedStyleName] = useState("");
  
  // Pending file upload (waiting for unit selection)
  const [pendingFile, setPendingFile] = useState(null);

  // Fetch door styles on mount
  useEffect(() => {
    async function fetchDoorStyles() {
      try {
        const res = await axios.get(API + "/door-styles");
        setDoorStyles(res.data);
      } catch (e) { 
        console.error(e); 
      }
    }
    fetchDoorStyles();
    setMsgs([{ r: "a", t: "Upload your door list, Excel file, order form, or photo of dimensions — or just type your question below.\n\nTry: \"How much for 2 doors at 13×30 inches?\"" }]);
  }, []);

  // Convert dimensions when units change
  useEffect(() => {
    if (prevUnitsRef.current !== units && doors.length > 0) {
      const prevUnit = prevUnitsRef.current;
      const newUnit = units;
      
      setDoors((currentDoors) => 
        currentDoors.map((door) => {
          const w = parseFraction(door.w) || 0;
          const h = parseFraction(door.h) || 0;
          
          if (w === 0 && h === 0) return door;
          
          // First convert to inches (base unit)
          let wInches = w;
          let hInches = h;
          if (prevUnit === "cm") {
            wInches = w / 2.54;
            hInches = h / 2.54;
          } else if (prevUnit === "mm") {
            wInches = w / 25.4;
            hInches = h / 25.4;
          }
          
          // Then convert to target unit
          let newW, newH;
          if (newUnit === "cm") {
            newW = wInches > 0 ? (wInches * 2.54).toFixed(4) : "";
            newH = hInches > 0 ? (hInches * 2.54).toFixed(4) : "";
          } else if (newUnit === "mm") {
            newW = wInches > 0 ? (wInches * 25.4).toFixed(4) : "";
            newH = hInches > 0 ? (hInches * 25.4).toFixed(4) : "";
          } else {
            // imperial (inches)
            newW = wInches > 0 ? wInches.toFixed(4) : "";
            newH = hInches > 0 ? hInches.toFixed(4) : "";
          }
          
          return { ...door, w: newW, h: newH };
        })
      );
    }
    prevUnitsRef.current = units;
  }, [units, doors.length]);

  // Auto-include packaging for shipped orders
  useEffect(() => {
    if (region !== "local_pickup") {
      setIncludePackaging(true);
    }
  }, [region]);

  // Calculate quote
  const calc = useCallback(async () => {
    if (doors.length === 0) { 
      setQuote(null); 
      return; 
    }
    setIsCalc(true);
    try {
      const d = doors.map((x) => { 
        let w = parseFraction(x.w) || 0;
        let h = parseFraction(x.h) || 0;
        // Convert to inches for calculation
        if (units === "cm") {
          w = w / 2.54;
          h = h / 2.54;
        } else if (units === "mm") {
          w = w / 25.4;
          h = h / 25.4;
        }
        return { 
          width: w, 
          height: h, 
          quantity: parseInt(x.q) || 1, 
          style: x.s || "cordova", 
          hinges: parseInt(x.hi) || 0, 
          primed: x.pr || false 
        }; 
      });
      const shouldIncludePackaging = region === "local_pickup" ? includePackaging : true;
      const r = await axios.post(API + "/calculate-quote", { 
        doors: d, 
        region: region, 
        include_packaging: shouldIncludePackaging 
      });
      setQuote(r.data);
    } catch (e) { 
      console.error(e); 
    }
    setIsCalc(false);
  }, [doors, region, includePackaging, units]);

  useEffect(() => { calc(); }, [calc]);
  
  // Scroll chat into view after user sends message
  useEffect(() => { 
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    if (chatRef.current && msgs.length > 1) {
      chatRef.current.scrollIntoView({ behavior: "smooth" }); 
    }
  }, [msgs]);

  // Door management functions
  function addDoor() { 
    setDoors((p) => [...p, { 
      id: Date.now(), 
      w: "", 
      h: "", 
      q: 1, 
      s: "cordova", 
      hi: 0, 
      pr: false, 
      type: "door", 
      name: "", 
      grain: "vertical" 
    }]); 
  }
  
  function updateDoor(id, key, value) { 
    setDoors((p) => p.map((x) => x.id === id ? { ...x, [key]: value } : x)); 
  }
  
  function deleteDoor(id) { 
    setDoors((p) => p.filter((x) => x.id !== id)); 
  }

  function addFromAI(arr) {
    const n = arr.map((x, i) => ({ 
      id: Date.now() + i, 
      w: String(x.width || ""), 
      h: String(x.height || ""), 
      q: x.quantity || 1, 
      s: x.style || "cordova", 
      hi: x.hinges || 0, 
      pr: x.primed || false, 
      type: x.type || "door" 
    }));
    setDoors((p) => [...p, ...n]); 
    toast.success("Added " + n.length + " part(s)");
  }

  // Image viewer
  function openImageViewer(styleKey) {
    const style = doorStyles[styleKey];
    if (style && style.image) {
      setSelectedImage(style.image);
      setSelectedStyleName(style.name + " - $" + style.price_per_sqft + "/sqft");
      setShowImageModal(true);
    }
  }

  // Chat functions
  async function sendMsg() {
    if (!chatIn.trim()) return;
    const m = chatIn; 
    setChatIn(""); 
    setMsgs((p) => [...p, { r: "u", t: m }]); 
    setLoading(true);
    setIsProcessingFile(true);
    try {
      const r = await axios.post(API + "/chat", { message: m, session_id: sessId });
      setSessId(r.data.session_id);
      setMsgs((p) => [...p, { r: "a", t: r.data.response }]);
      if (r.data.extracted_doors && r.data.extracted_doors.doors) {
        addFromAI(r.data.extracted_doors.doors);
      }
    } catch (e) { 
      setMsgs((p) => [...p, { r: "a", t: "Error. Please try again." }]); 
    }
    setLoading(false);
    setIsProcessingFile(false);
  }

  // Handle file selection - show unit modal first for ALL file types
  function handleFileSelect(f) {
    if (!f) return;
    setPendingFile(f);
    setShowUnitModal(true);
  }

  // Process file upload with selected unit
  async function uploadFile(f, selectedUnit = "inches") {
    if (!f) return;
    const fd = new FormData(); 
    fd.append("file", f); 
    fd.append("message", `Extract door dimensions. The measurements in this file are in ${selectedUnit}. Convert to inches if needed.`);
    fd.append("unit", selectedUnit);
    setMsgs((p) => [...p, { r: "u", t: "📎 " + f.name + ` (${selectedUnit})` }]); 
    setLoading(true);
    setIsProcessingFile(true);
    try {
      const r = await axios.post(API + "/upload-and-analyze", fd, { 
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000 // 3 minutes timeout for large files
      });
      setSessId(r.data.session_id); 
      setMsgs((p) => [...p, { r: "a", t: r.data.response }]);
      if (r.data.extracted_doors && r.data.extracted_doors.doors) {
        addFromAI(r.data.extracted_doors.doors);
      }
    } catch (e) { 
      console.error("Upload error details:", e.response?.data || e.message || e);
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        setMsgs((p) => [...p, { r: "a", t: "The file is taking longer to process. Please wait a moment and try again, or enter dimensions manually." }]);
      } else if (e.response?.data?.detail) {
        setMsgs((p) => [...p, { r: "a", t: `Error: ${e.response.data.detail}` }]);
      } else {
        setMsgs((p) => [...p, { r: "a", t: "File error. Please try again or enter your dimensions manually." }]); 
      }
    }
    setLoading(false);
    setIsProcessingFile(false);
  }

  // Handle unit selection for pending file
  function handleUnitSelect(unit) {
    setShowUnitModal(false);
    if (pendingFile) {
      // Set the units toggle to match the selected unit
      const unitMapping = {
        "inches": "imperial",
        "cm": "cm",
        "mm": "mm"
      };
      setUnits(unitMapping[unit] || "imperial");
      uploadFile(pendingFile, unit);
      setPendingFile(null);
    }
  }

  // PDF download
  async function getPDF() {
    if (!quote) return;
    try {
      const r = await axios.post(API + "/generate-pdf", quote, { responseType: "blob" });
      const u = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); 
      a.href = u; 
      a.download = "quote.pdf"; 
      document.body.appendChild(a); 
      a.click(); 
      a.remove();
      toast.success("PDF downloaded!");
    } catch (e) { 
      toast.error("PDF failed"); 
    }
  }

  // Add to Shopify Cart - Opens contact modal first
  function addToCart() {
    if (!quote) return;
    setShowContactModal(true);
  }

  // Form submissions
  async function submitContact() {
    if (!contactForm.name || !contactForm.email || !contactForm.phone) { 
      toast.error("Fill all fields"); 
      return; 
    }
    
    // Check if we should create Shopify checkout
    if (quote) {
      try {
        toast.loading("Creating your order...");
        
        const response = await createShopifyCheckout(
          quote, 
          doors, 
          region, 
          {
            name: contactForm.name,
            email: contactForm.email,
            phone: contactForm.phone
          },
          API
        );
        
        toast.dismiss();
        
        if (response.checkout_url) {
          toast.success("Redirecting to checkout...", { duration: 2000 });
          setShowContactModal(false);
          setContactForm({ name: "", email: "", phone: "", notes: "" });
          
          // Redirect to Shopify checkout - use top to break out of iframe
          setTimeout(() => {
            window.top.location.href = response.checkout_url;
          }, 1000);
          return;
        }
      } catch (error) {
        toast.dismiss();
        console.error("Checkout error:", error);
        toast.error(error.message || "Failed to create order. Saving your contact info instead.");
        // Fall through to regular contact submission
      }
    }
    
    // Fallback: Save contact info if Shopify checkout fails
    try {
      await axios.post(API + "/submit-contact", { 
        name: contactForm.name, 
        email: contactForm.email, 
        phone: contactForm.phone, 
        notes: contactForm.notes, 
        quote_data: quote 
      });
      toast.success("Submitted! We'll contact you soon."); 
      setShowContactModal(false); 
      setContactForm({ name: "", email: "", phone: "", notes: "" });
    } catch (e) { 
      toast.error("Submit failed"); 
    }
  }

  async function submitCallback() {
    if (!callbackForm.name || !callbackForm.phone) { 
      toast.error("Name and phone required"); 
      return; 
    }
    try {
      await axios.post(API + "/request-callback", { 
        name: callbackForm.name, 
        phone: callbackForm.phone, 
        preferred_time: callbackForm.time, 
        notes: callbackForm.notes 
      });
      toast.success("Callback requested! We'll call you soon."); 
      setShowCallbackModal(false); 
      setCallbackForm({ name: "", phone: "", time: "", notes: "" });
    } catch (e) { 
      toast.error("Request failed"); 
    }
  }

  async function sendEmail() {
    if (!emailForm.email || !emailForm.name) { 
      toast.error("Email and name required"); 
      return; 
    }
    try {
      const r = await axios.post(API + "/email-quote", { 
        recipient_email: emailForm.email, 
        customer_name: emailForm.name, 
        quote_data: quote 
      });
      if (r.data.email_pending) { 
        toast.success("Quote saved! Email will be sent when configured."); 
      } else { 
        toast.success("Quote emailed!"); 
      }
      setShowEmailModal(false); 
      setEmailForm({ name: "", email: "" });
    } catch (e) { 
      toast.error("Email failed"); 
    }
  }

  async function saveQuote() {
    if (!quote) return;
    try {
      const r = await axios.post(API + "/save-quote", { 
        customer_name: saveForm.name, 
        customer_email: saveForm.email, 
        quote_data: quote 
      });
      setSavedQuoteId(r.data.quote_id);
      toast.success("Quote saved! ID: " + r.data.quote_id); 
      setShowSaveModal(false); 
      setSaveForm({ name: "", email: "" });
    } catch (e) { 
      toast.error("Save failed"); 
    }
  }

  async function loadQuote() {
    if (!loadId) { 
      toast.error("Enter quote ID"); 
      return; 
    }
    try {
      const r = await axios.get(API + "/load-quote/" + loadId);
      if (r.data.quote_data && r.data.quote_data.doors) {
        const newDoors = r.data.quote_data.doors.map((d, i) => ({
          id: Date.now() + i, 
          w: String(d.width), 
          h: String(d.height), 
          q: d.quantity, 
          s: d.style_key || "cordova", 
          hi: d.hinges || 0, 
          pr: d.primed || false 
        }));
        setDoors(newDoors);
        toast.success("Quote loaded!");
      }
      setShowLoadModal(false); 
      setLoadId("");
    } catch (e) { 
      toast.error("Quote not found"); 
    }
  }

  // Form change handlers
  function handleContactFormChange(key, value) {
    setContactForm(prev => ({ ...prev, [key]: value }));
  }

  function handleCallbackFormChange(key, value) {
    setCallbackForm(prev => ({ ...prev, [key]: value }));
  }

  function handleEmailFormChange(key, value) {
    setEmailForm(prev => ({ ...prev, [key]: value }));
  }

  function handleSaveFormChange(key, value) {
    setSaveForm(prev => ({ ...prev, [key]: value }));
  }

  // Embedded mode - set to true when embedding in Shopify
  const embedded = true;

  return (
    <div className={embedded ? "bg-transparent" : "min-h-screen bg-white"}>
      <Toaster position="top-right" richColors />
      
      <ProcessingOverlay isVisible={isProcessingFile} />
      
      <Header 
        onLoadQuote={() => setShowLoadModal(true)} 
        onRequestCallback={() => setShowCallbackModal(true)}
        embedded={embedded}
      />

      <main className={embedded ? "px-0 py-2" : "max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8"}>
        {/* Title Section - Always show */}
        <h1 className="font-sans text-2xl sm:text-4xl md:text-5xl font-bold text-center mb-2 text-[#1A1A1A]">
          Get Your Custom Quote
        </h1>
        <p className="text-center text-gray-500 text-sm sm:text-base mb-4 font-sans">
          AI Powered Instant Pricing. The easiest way to get a quote for cabinet doors, drawer fronts or panels.
        </p>
        
        {/* Action Buttons */}
        <div className="flex justify-center gap-3 mb-6 sm:mb-8">
          <button
            onClick={() => setShowLoadModal(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#6A7359] bg-white border border-[#6A7359] rounded-md hover:bg-[#6A7359]/5 transition-colors"
            data-testid="load-quote-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
            </svg>
            Load Quote
          </button>
          <button
            onClick={() => setShowCallbackModal(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#6A7359] rounded-md hover:bg-[#565E48] transition-colors"
            data-testid="callback-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Request Callback
          </button>
        </div>

        <div className="grid lg:grid-cols-5 gap-4 sm:gap-8">
          <div className="lg:col-span-3 space-y-4 sm:space-y-6">
            <ChatWindow
              messages={msgs}
              chatInput={chatIn}
              onChatInputChange={setChatIn}
              onSendMessage={sendMsg}
              onFileUpload={handleFileSelect}
              loading={loading}
              chatRef={chatRef}
            />

            <OrderBuilder
              doors={doors}
              doorStyles={doorStyles}
              units={units}
              onUnitsChange={setUnits}
              onAddDoor={addDoor}
              onUpdateDoor={updateDoor}
              onDeleteDoor={deleteDoor}
              onViewImage={openImageViewer}
            />
          </div>

          <div className="lg:col-span-2">
            <QuoteSummary
              quote={quote}
              region={region}
              onRegionChange={setRegion}
              includePackaging={includePackaging}
              onIncludePackagingChange={setIncludePackaging}
              isCalculating={isCalc}
              onRefresh={calc}
              onSave={() => setShowSaveModal(true)}
              onEmail={() => setShowEmailModal(true)}
              onDownloadPDF={getPDF}
              onProceed={addToCart}
              savedQuoteId={savedQuoteId}
              onLoadQuote={() => setShowLoadModal(true)}
              embedded={embedded}
            />
          </div>
        </div>
      </main>

      <Footer embedded={embedded} />

      {/* Modals */}
      <ImageViewerModal
        open={showImageModal}
        onOpenChange={setShowImageModal}
        imageUrl={selectedImage}
        styleName={selectedStyleName}
      />

      <ContactModal
        open={showContactModal}
        onOpenChange={setShowContactModal}
        form={contactForm}
        onFormChange={handleContactFormChange}
        onSubmit={submitContact}
      />

      <CallbackModal
        open={showCallbackModal}
        onOpenChange={setShowCallbackModal}
        form={callbackForm}
        onFormChange={handleCallbackFormChange}
        onSubmit={submitCallback}
      />

      <EmailModal
        open={showEmailModal}
        onOpenChange={setShowEmailModal}
        form={emailForm}
        onFormChange={handleEmailFormChange}
        onSubmit={sendEmail}
      />

      <SaveQuoteModal
        open={showSaveModal}
        onOpenChange={setShowSaveModal}
        form={saveForm}
        onFormChange={handleSaveFormChange}
        onSubmit={saveQuote}
      />

      <LoadQuoteModal
        open={showLoadModal}
        onOpenChange={setShowLoadModal}
        quoteId={loadId}
        onQuoteIdChange={setLoadId}
        onSubmit={loadQuote}
      />

      <UnitSelectModal
        open={showUnitModal}
        onOpenChange={setShowUnitModal}
        onSelect={handleUnitSelect}
        fileName={pendingFile?.name || ""}
      />
    </div>
  );
}

export default App;
